<template>
  <div class="h-full some-media">
    <video-js
      ref="playerRef"
    />
    <slot>
      <img
        v-if="model.poster && typeof model.poster === 'string'"
        :src="model.poster"
        :alt="model.heading"
        :width="width || '200'"
        :height="height || '100'"
        :loading="{'lazy': isLazy}"
        class="absolute object-cover object-center w-full h-full z-10 top-0"
        :class="videoStarted ? 'hidden' : ''"
      >
    </slot>
    <div
      v-if="showBtnPlay"
      class="absolute top-0 left-0 w-full h-full cursor-pointer z-20"
      @click="onHandleClick"
    >
      <button class="w-16 h-16 flex bg-primary-red text-black items-center justify-center rounded-full shadow absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <svg xmlns="http://www.w3.org/2000/svg"
             width="14"
             height="14"
             viewBox="0 0 18 18"
             fill="none"
        >
          <path d="M0 17.3823V0.582275L18 8.98228L0 17.3823Z"
                fill="white"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import {
  onMounted, ref, computed, watch
} from 'vue'
import videojs from 'video.js/dist/video'
import 'video.js/dist/video-js.css'

export default {
  props: {
    model: {
      type: Object,
      required: true
    },
    isCarouselReady: {
      type: Boolean,
      default: false
    },
    carouselInstance: {
      type: Object,
      default: null
    },
    isActiveParent: {
      type: Boolean,
      default: true
    },
    isLazy: {
      type: Boolean,
      default: false
    },
    width: {
      type: String,
      default: ''
    },
    height: {
      type: String,
      default: ''
    }
  },
  setup(props, { emit }) {
    let player
    const playerRef = ref()
    const trackingDuration = []
    const availableTrackingDurations = [90, 75, 50, 25, 10]
    const showBtnPlay = ref(!props.model.autoplay)
    const showPlayBtnOverlay = ref(props.model.autoplay)
    const videoStarted = ref(false)

    const src = computed(() => {
      if (window.innerWidth > 768) {
        return props.model.src
      }
      return props.model.mobileSrc || props.model.src
    })

    const videoOptions = computed(() => {
      const {
        autoplay,
        loop,
        muted,
        controls
      } = props.model

      return {
        autoplay,
        loop,
        muted,
        controls,
        playsinline: true,
        liveui: controls,
        sources: [
          {
            src: src.value,
            type: 'application/x-mpegURL'
          }
        ]
      }
    })

    const pushDataLayer = (obj) => {
      if (window.dataLayer) {
        window.dataLayer.push(obj)
      }
    }

    const videoStart = (heading) => {
      const gtmObj = {
        event: 'videoStart',
        videoTitle: heading,
        placement: window.gtmData?.pageName
      }
      pushDataLayer(gtmObj)
    }

    const videoComplete = (heading) => {
      const gtmObj = {
        event: 'videoComplete',
        videoTitle: heading
      }
      pushDataLayer(gtmObj)
    }

    const videoProgress = (heading, duration) => {
      const gtmObj = {
        event: 'videoProgress',
        videoPercent: `${duration}%`,
        videoTitle: heading
      }
      pushDataLayer(gtmObj)
    }

    const onHandleClick = () => {
      if (showPlayBtnOverlay) {
        player.play()
        emit('videoPlayPressed', player)
        showPlayBtnOverlay.value = false
        showBtnPlay.value = false
        videoStarted.value = true
        videoStart(props.model.heading)
      }
    }

    const getPercent = (tempPercent, percentValue) => {
      if (tempPercent > percentValue && !trackingDuration.includes(percentValue)) {
        return percentValue
      }
      return 0
    }

    const checkState = () => {
      if (!props.isCarouselReady) {
        return
      }

      if (props.isCarouselReady) {
        player.on('canplay', () => {
          player.pause()

          const activeSlideWrapper = playerRef.value?.closest('.swiper-slide-active')

          if (activeSlideWrapper) {
            player.play()
          }
        })
      }
      props.carouselInstance.on('slideChangeTransitionEnd', () => {
        const { activeIndex } = props.carouselInstance
        const currentSlide = props.carouselInstance.slides[activeIndex]
        const videoWrapper = playerRef.value?.closest('.swiper-slide-active')

        if (videoWrapper === currentSlide) {
          player.play()
        } else {
          player.pause()
        }
      })
    }

    onMounted(() => {
      if (!props.model.autoplay) {
        videoOptions.value.muted = false
        videoOptions.value.controls = true
      }

      if (props.model.bestQuality) {
        videoOptions.value.html5 = {
          hls: {
            limitRenditionByPlayerDimensions: false,
            bandwidth: 6625071
          }
        }
      }

      player = videojs(playerRef.value, videoOptions.value, () => {
        /* eslint-disable-next-line */
        const videoTag = player.el_.querySelector('video')
        videoTag.setAttribute('aria-label', props.model.description)
      })

      if (!props.model.autoplay) {
        player.on('ended', () => {
          videoComplete(props.model.heading)
        })
        player.on('timeupdate', () => {
          const tempPercent = Math.trunc((player.currentTime() * 100) / player.duration())
          let percent = 0
          for (let i = 0; i < availableTrackingDurations.length; i += 1) {
            percent = getPercent(tempPercent, availableTrackingDurations[i])
            if (percent !== 0) {
              break
            }
          }

          if (percent !== 0 && !trackingDuration.includes(percent)
            && availableTrackingDurations.includes(percent)) {
            videoProgress(props.model.heading, percent)
            trackingDuration.push(percent)
          }
        })
      }
    })

    watch(() => props.carouselInstance, () => {
      checkState()
    })
    watch(() => props.isActiveParent, (value) => {
      if (value) {
        player.play()
      } else {
        player.pause()
      }
    })

    return {
      playerRef,
      showPlayBtnOverlay,
      showBtnPlay,
      videoStarted,
      onHandleClick
    }
  }
}

</script>
